import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiRefreshCw,
  FiList,
  FiPlay,
  FiPause,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiLayers,
  FiArrowRight,
  FiCheck,
  FiClipboard,
  FiEye,
  FiEdit2,
} from "react-icons/fi";

import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { withAuth } from "../../../components/HOC/withAuth";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import { buildRadioFilterSection } from "../shared/buildListFilterSections";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";

import styles from "./WorkOrders.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const STATUS_LABELS = {
  draft: "Taslak",
  planned: "Planlandı",
  pending: "Sırada",
  in_progress: "Üretimde",
  paused: "Durduruldu",
  completed: "Tamamlandı",
  cancelled: "İptal Edildi",
};

const STATUS_APPEARANCES = {
  draft: "default-subtle",
  planned: "info-subtle",
  pending: "info-subtle",
  in_progress: "warning-subtle",
  paused: "danger-subtle",
  completed: "success-subtle",
  cancelled: "neutral-subtle",
};

const PRIORITY_LABELS = {
  urgent: "Acil",
  high: "Yüksek",
  normal: "Normal",
  low: "Düşük",
};

const PRIORITY_APPEARANCES = {
  urgent: "danger-subtle",
  high: "warning-subtle",
  normal: "info-subtle",
  low: "default-subtle",
};

const TABLE_COLUMNS = [
  { name: "", dataType: "text", id: 0, sortable: false, width: 44 },
  {
    name: "İş Emri No",
    dataType: "text",
    id: 1,
    width: 150,
    customSorter: (a, b) =>
      String(a.workOrderNumber || "").localeCompare(String(b.workOrderNumber || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
  },
  {
    name: "Model / Parça",
    dataType: "text",
    id: 2,
    customSorter: (a, b) => {
      const nameA = a.deviceModelId?.name || a.partId?.name || a.title || "";
      const nameB = b.deviceModelId?.name || b.partId?.name || b.title || "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    },
  },
  {
    name: "Sipariş",
    dataType: "text",
    id: 3,
    width: 120,
    customSorter: (a, b) =>
      String(a.saleId?.saleName || "").localeCompare(String(b.saleId?.saleName || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
  },
  {
    name: "Öncelik",
    dataType: "text",
    id: 4,
    width: 130,
    customSorter: (a, b) => (a.priorityRank || 99) - (b.priorityRank || 99),
  },
  {
    name: "İlerleme",
    dataType: "number",
    id: 5,
    width: 170,
    customSorter: (a, b) => {
      const pctA = a.plannedQuantity > 0 ? (a.producedQuantity || 0) / a.plannedQuantity : 0;
      const pctB = b.plannedQuantity > 0 ? (b.producedQuantity || 0) / b.plannedQuantity : 0;
      return pctA - pctB;
    },
  },
  {
    name: "Tarihler",
    dataType: "date",
    id: 6,
    width: 130,
    customSorter: (a, b) => {
      const ta = a.plannedStartDate ? new Date(a.plannedStartDate).getTime() : 0;
      const tb = b.plannedStartDate ? new Date(b.plannedStartDate).getTime() : 0;
      return ta - tb;
    },
  },
  {
    name: "Durum",
    dataType: "text",
    id: 7,
    width: 120,
    customSorter: (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
  },
  { name: "İşlemler", id: 8, sortable: false, width: 140 },
];

const TOGGLEABLE_COLUMNS = TABLE_COLUMNS.filter((c) => c.name);

export const WorkOrders = () => {
  useTitle("Rovion MES - İş Emirleri");
  const { getToken, user: authUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const currentUser = authUser;
  const isOperator =
    !currentUser?.fullAccess &&
    (currentUser?.isOperator ||
      currentUser?.profileId?.isOperator ||
      currentUser?.role === "employee");
  const canWrite =
    !currentUser ||
    hasPageWriteAccess(
      currentUser.role,
      "/work-orders",
      currentUser.pages || [],
      !!currentUser.fullAccess
    );

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("work-orders-list", TOGGLEABLE_COLUMNS);

  const [toasts, setToasts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalOrder, setDetailModalOrder] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [expandedOrders, setExpandedOrders] = useState({});
  const activeScope = isOperator ? "mine" : "all";

  const filterSections = useMemo(
    () => [
      buildRadioFilterSection({
        id: "status",
        title: "Durum",
        value: filterStatus,
        onChange: (v) => setFilterStatus(v || ""),
        options: [
          { value: "", label: "Tümü" },
          { value: "planned", label: "Planlandı" },
          { value: "in_progress", label: "Üretimde" },
          { value: "paused", label: "Durduruldu" },
          { value: "completed", label: "Tamamlandı" },
          { value: "cancelled", label: "İptal Edildi" },
        ],
      }),
      buildRadioFilterSection({
        id: "priority",
        title: "Öncelik",
        value: filterPriority,
        onChange: (v) => setFilterPriority(v || ""),
        options: [
          { value: "", label: "Tümü" },
          { value: "urgent", label: "Acil" },
          { value: "high", label: "Yüksek" },
          { value: "normal", label: "Normal" },
          { value: "low", label: "Düşük" },
        ],
      }),
    ],
    [filterStatus, filterPriority]
  );

  const activeFilterCount = (filterStatus ? 1 : 0) + (filterPriority ? 1 : 0);

  const handleResetFilters = useCallback(() => {
    setFilterStatus("");
    setFilterPriority("");
  }, []);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken]
  );

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    const appearance = type === "error" ? "danger" : type;
    const title =
      appearance === "danger"
        ? "Hata!"
        : appearance === "warning"
        ? "Uyarı!"
        : appearance === "info"
        ? "Bilgi"
        : "Başarılı!";
    setToasts((prev) => [
      ...prev,
      { id, title, description: message, message, appearance },
    ]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      5000
    );
  }, []);

  const fetchOrders = useCallback(async () => {
    if (authLoading) return;
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (activeScope) params.scope = activeScope;

      const res = await axios.get(`${API_URL}/api/work-orders`, {
        headers: authHeaders(),
        params,
      });

      setOrders(res.data?.data || []);
    } catch (err) {
      console.error("Fetch work orders error:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filterStatus, filterPriority, activeScope, authLoading]);

  useEffect(() => {
    if (!authLoading) {
      fetchOrders();
    }
  }, [fetchOrders, authLoading]);

  const toggleExpand = (id) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (!canWrite) {
      addToast("Bu işlem için düzenleme yetkiniz bulunmuyor.", "error");
      return;
    }
    try {
      await axios.patch(
        `${API_URL}/api/work-orders/${orderId}/status`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      addToast(`İş emri durumu '${STATUS_LABELS[newStatus] || newStatus}' yapıldı.`);
      fetchOrders();
    } catch (err) {
      addToast(err?.response?.data?.message || "Durum güncellenemedi.", "error");
    }
  };

  const handlePriorityChange = async (orderId, newPriority) => {
    if (!canWrite) {
      addToast("Bu işlem için düzenleme yetkiniz bulunmuyor.", "error");
      return;
    }
    try {
      await axios.patch(
        `${API_URL}/api/work-orders/${orderId}/priority`,
        { priority: newPriority },
        { headers: authHeaders() }
      );
      addToast(`Öncelik '${PRIORITY_LABELS[newPriority] || newPriority}' olarak güncellendi.`);
      fetchOrders();
    } catch (err) {
      addToast(err?.response?.data?.message || "Öncelik güncellenemedi.", "error");
    }
  };

  const handleAutoStartToggle = async (orderId, currentVal) => {
    if (!canWrite) {
      addToast("Bu işlem için düzenleme yetkiniz bulunmuyor.", "error");
      return;
    }
    try {
      const nextVal = currentVal === false ? true : false;
      await axios.patch(
        `${API_URL}/api/work-orders/${orderId}/auto-start`,
        { autoStart: nextVal },
        { headers: authHeaders() }
      );
      addToast(nextVal ? "Otomatik başlatma etkinleştirildi." : "Manuel başlatma moduna alındı.");
      fetchOrders();
    } catch (err) {
      addToast(err?.response?.data?.message || "Ayar güncellenemedi.", "error");
    }
  };

  const handleDelete = (orderId) => {
    confirm({
      title: "İş Emrini Sil",
      message: "Bu iş emrini silmek istediğinize emin misiniz?",
      appearance: "danger",
      confirmButtonText: "Sil",
      cancelButtonText: "İptal",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/work-orders/${orderId}`, {
            headers: authHeaders(),
          });
          addToast("İş emri silindi.");
          fetchOrders();
        } catch (err) {
          addToast(err?.response?.data?.message || "İş emri silinemedi.", "error");
        }
      },
    });
  };

  return (
    <AppPage
      loading={loading || authLoading}
      title={isOperator && activeScope === "mine" ? "Çalışılan Adımlar (Görev Listem)" : "İş Emirleri"}
      subtitle={
        isOperator && activeScope === "mine"
          ? "Size ve makinelerinize ait aktif olarak üzerinde çalışılan operasyon adımları"
          : "İş emirlerini görüntüleyin, önceliklendirin ve yönetin"
      }
      icon={<FiClipboard />}
      actions={[
        <Button
          key="refresh"
          appearance="subtle"
          onClick={fetchOrders}
          disabled={loading || authLoading}
          iconBefore={<FiRefreshCw size={14} className={loading || authLoading ? "spin" : ""} />}
        >
          Yenile
        </Button>,
        canWrite && (
          <Button
            key="create"
            appearance="primary-animated"
            onClick={() => navigate("/work-orders/create")}
            iconBefore={<FiPlus size={16} />}
          >
            Yeni İş Emri Oluştur
          </Button>
        ),
      ].filter(Boolean)}
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            description={t.description || t.message}
            appearance={t.appearance}
            onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>

      <CardContainer>
        {/* Orders Table */}
        <Table
          columns={TABLE_COLUMNS}
          visibleColumns={[0, ...visibleColumns]}
          tableId="work-orders-list"
          loading={loading || authLoading}
          search={true}
          searchPlaceholder="İş Emri No, Model, Sipariş veya Durumda Ara..."
          hideFooter
          viewOptions={
            <ListViewOptionsPanel
              columns={TOGGLEABLE_COLUMNS}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              filterSections={filterSections}
              activeFilterCount={activeFilterCount}
              onApply={fetchOrders}
              onReset={handleResetFilters}
            />
          }
        >
          {orders.map((order) => {
            const isExpanded = !!expandedOrders[order._id];
            const finalStage = order.stages && order.stages.length > 0 ? order.stages[order.stages.length - 1] : null;
            const finalProducedQty = finalStage ? (finalStage.producedQuantity || 0) : (order.producedQuantity || 0);
            const pct =
              order.plannedQuantity > 0
                ? Math.min(
                    100,
                    Math.round(
                      (finalProducedQty / order.plannedQuantity) * 100
                    )
                  )
                : 0;

            const targetName = order.deviceModelId
              ? `${order.deviceModelId.name} (${order.deviceModelId.code})`
              : order.partId
              ? `${order.partId.name} (${order.partId.code})`
              : order.title;

            return (
              <React.Fragment key={order._id}>
                <Tr
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleExpand(order._id)}
                  data={order}
                >
                  <Td style={{ width: 36, textAlign: "center" }}>
                    {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                  </Td>
                  {visibleColumns.includes(1) && (
                    <Td>
                      <span style={{ fontWeight: 700, color: "var(--m-primary, #6366f1)" }}>
                        {order.workOrderNumber}
                      </span>
                    </Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>
                      <div style={{ fontWeight: 600 }}>{targetName}</div>
                      {isOperator && activeScope === "mine" ? (
                        <div style={{ fontSize: 11, color: "var(--m-primary, #6366f1)", fontWeight: 600, marginTop: 2 }}>
                          {order.myStages?.length || 0} Adım Üzerinde Çalışılıyor
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: "var(--m-text-subtle, #6b7280)" }}>
                          {order.type === "model" ? "Ürün Modeli" : "Parça Üretimi"} —{" "}
                          {order.stages?.length || 0} Aşama
                        </div>
                      )}
                      {order.myStages && order.myStages.length > 0 && (
                        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {order.myStages.map((st, sIdx) => (
                            <span
                              key={sIdx}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                background: "#ecfdf5",
                                color: "#065f46",
                                border: "1px solid #a7f3d0",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                              title={`Aşama ${st.stageOrder}: ${st.partName}`}
                            >
                              ★ Çalışılan Adım: Aşama {st.stageOrder} ({st.partName}) — {st.producedQuantity || 0}/{st.plannedQuantity} Adet
                            </span>
                          ))}
                        </div>
                      )}
                    </Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td>
                      {order.saleId ? (
                        <div>
                          <span style={{ fontWeight: 600 }}>{order.saleId.saleName || "Sipariş"}</span>
                        </div>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      )}
                    </Td>
                  )}
                  {visibleColumns.includes(4) && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      {canWrite ? (
                        <select
                          value={order.priority || "normal"}
                          onChange={(e) => handlePriorityChange(order._id, e.target.value)}
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "3px 6px",
                            borderRadius: 6,
                            border: "1px solid var(--m-border-color, #e5e7eb)",
                            background:
                              order.priority === "urgent"
                                ? "#fee2e2"
                                : order.priority === "high"
                                ? "#fef3c7"
                                : "#eff6ff",
                            color:
                              order.priority === "urgent"
                                ? "#991b1b"
                                : order.priority === "high"
                                ? "#92400e"
                                : "#1e40af",
                            cursor: "pointer",
                            width: "100%",
                            maxWidth: 105,
                          }}
                          title="Önceliği Değiştir"
                        >
                          <option value="urgent">Acil</option>
                          <option value="high">Yüksek</option>
                          <option value="normal">Normal</option>
                          <option value="low">Düşük</option>
                        </select>
                      ) : (
                        <span
                          style={{
                            display: "inline-block",
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "3px 8px",
                            borderRadius: 6,
                            background:
                              order.priority === "urgent"
                                ? "#fee2e2"
                                : order.priority === "high"
                                ? "#fef3c7"
                                : "#eff6ff",
                            color:
                              order.priority === "urgent"
                                ? "#991b1b"
                                : order.priority === "high"
                                ? "#92400e"
                                : "#1e40af",
                          }}
                        >
                          {PRIORITY_LABELS[order.priority] || order.priority}
                        </span>
                      )}
                    </Td>
                  )}
                  {visibleColumns.includes(5) && (
                    <Td>
                      <div className={styles.progressWrap}>
                        <div className={styles.progressTrack}>
                          <div
                            className={styles.progressFill}
                            style={{
                              width: `${pct}%`,
                              background:
                                pct === 100
                                  ? "#10b981"
                                  : pct > 50
                                  ? "#f59e0b"
                                  : "var(--m-primary, #6366f1)",
                            }}
                          />
                        </div>
                        <span className={styles.progressPct}>{pct}%</span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--m-text-subtle, #6b7280)", marginTop: 2 }}>
                        {finalProducedQty} / {order.plannedQuantity} Adet
                      </div>
                    </Td>
                  )}
                  {visibleColumns.includes(6) && (
                    <Td style={{ fontSize: 12 }}>
                      <div>
                        {order.plannedStartDate
                          ? new Date(order.plannedStartDate).toLocaleDateString("tr-TR")
                          : "—"}
                      </div>
                      <div style={{ color: "var(--m-text-subtle, #6b7280)" }}>
                        {order.plannedEndDate
                          ? new Date(order.plannedEndDate).toLocaleDateString("tr-TR")
                          : "—"}
                      </div>
                    </Td>
                  )}
                  {visibleColumns.includes(7) && (
                    <Td>
                      <Lozenge
                        appearance={
                          STATUS_APPEARANCES[order.status] || "default-subtle"
                        }
                        isBold
                      >
                        {STATUS_LABELS[order.status] || order.status}
                      </Lozenge>
                    </Td>
                  )}
                  {visibleColumns.includes(8) && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex align-items-center gap-1">
                        {canWrite && order.status === "planned" && (
                          <IconButton
                            icon={<FiPlay style={{ fontSize: "16px", color: "#16a34a" }} />}
                            appearance="subtle"
                            shape="circle"
                            tooltip="Üretime Başla"
                            onClick={() => handleStatusChange(order._id, "in_progress")}
                          />
                        )}
                        {canWrite && order.status === "in_progress" && (
                          <IconButton
                            icon={<FiPause style={{ fontSize: "16px", color: "#d97706" }} />}
                            appearance="subtle"
                            shape="circle"
                            tooltip="Durdur"
                            onClick={() => handleStatusChange(order._id, "paused")}
                          />
                        )}
                        {canWrite && order.status === "paused" && (
                          <IconButton
                            icon={<FiPlay style={{ fontSize: "16px", color: "#2563eb" }} />}
                            appearance="subtle"
                            shape="circle"
                            tooltip="Devam Et"
                            onClick={() => handleStatusChange(order._id, "in_progress")}
                          />
                        )}
                        {canWrite && order.status !== "completed" && (
                          <IconButton
                            icon={<FiCheck style={{ fontSize: "18px", color: "#10b981" }} />}
                            appearance="subtle"
                            shape="circle"
                            tooltip="Tamamla"
                            onClick={() => handleStatusChange(order._id, "completed")}
                          />
                        )}
                        <IconButton
                          icon={<FiEye style={{ fontSize: "18px" }} />}
                          appearance="subtle"
                          shape="circle"
                          tooltip="Detay Gör"
                          onClick={() => setDetailModalOrder(order)}
                        />
                        {canWrite && (
                          <IconButton
                            icon={<FiEdit2 style={{ fontSize: "16px" }} />}
                            appearance="subtle"
                            shape="circle"
                            tooltip="Düzenle"
                            onClick={() => navigate(`/work-orders/edit/${order._id}`)}
                          />
                        )}
                        {canWrite && (
                          <IconButton
                            icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                            appearance="subtle"
                            shape="circle"
                            tooltip="Sil"
                            onClick={() => handleDelete(order._id)}
                          />
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>

                {/* Expanded Row: Visual Pipeline Schema */}
                {isExpanded && (
                  <Tr>
                    <Td
                      colSpan={visibleColumns.length + 1}
                      className={styles.expandedCell}
                    >
                      <div className={styles.pipelineWrapper}>
                        <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                            <FiLayers size={16} color="var(--m-primary, #6366f1)" />
                            <span>
                              {isOperator
                                ? "Üzerinde Çalışılan Adımlar"
                                : "Üretim Hattı ve Aşamalar"}
                            </span>
                            {isOperator && (
                              <span
                                style={{
                                  background: "#ecfdf5",
                                  color: "#047857",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  border: "1px solid #a7f3d0",
                                }}
                              >
                                {order.stages?.length || 0} Aktif Adım
                              </span>
                            )}
                          </div>
                          {order.instructions && (
                            <div style={{ fontSize: 12, color: "#475569" }}>
                              <strong>Üretim Talimatı:</strong> {order.instructions}
                            </div>
                          )}
                        </div>

                        <div className={styles.pipelineContainer}>
                          {(order.stages || []).map((stage, idx) => {
                            const isMyStage =
                              (order.myStages || []).some((ms) => ms.stageOrder === stage.stageOrder) ||
                              (stage.assignedOperators || []).some(
                                (u) => String(u?._id || u) === String(currentUser?._id)
                              );

                            return (
                              <div key={idx} className={styles.pipelineStep}>
                                <div
                                  className={`${styles.stageCard} ${
                                    stage.isFulfilledFromStock ? styles.fulfilledFromStock : ""
                                  } ${stage.status === "in_progress" ? styles.activeStage : ""}`}
                                  style={
                                    isMyStage
                                      ? {
                                          borderColor: "#10b981",
                                          boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.3)",
                                          background: "linear-gradient(to bottom, #ffffff, #f0fdf4)",
                                        }
                                      : undefined
                                  }
                                >
                                  {isMyStage && (
                                    <div
                                      style={{
                                        background: "#10b981",
                                        color: "#ffffff",
                                        fontSize: 10,
                                        fontWeight: 800,
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        textAlign: "center",
                                        marginBottom: 6,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      ★ Sizin Göreviniz
                                    </div>
                                  )}
                                  <div className={styles.stageHeader}>
                                    <span className={styles.stageNumberBadge}>{stage.stageOrder}</span>
                                    <Lozenge
                                      appearance={
                                        stage.isFulfilledFromStock
                                          ? "default-subtle"
                                          : stage.status === "in_progress"
                                          ? "warning-subtle"
                                          : stage.status === "completed"
                                          ? "success-subtle"
                                          : "info-subtle"
                                      }
                                      isBold
                                    >
                                      {stage.isFulfilledFromStock
                                        ? "Stoktan Ayrıldı"
                                        : STATUS_LABELS[stage.status] || stage.status}
                                    </Lozenge>
                                  </div>

                                  <div className={styles.stagePartName}>{stage.partName || "Parça"}</div>
                                  <div className={styles.stagePartCode}>{stage.partCode || "—"}</div>

                                  <div className={styles.stageMetrics}>
                                    <div>
                                      Hedef: <strong>{stage.plannedQuantity} Adet</strong>
                                    </div>
                                    <div>
                                      Üretilen:{" "}
                                      <strong style={{ color: "#16a34a" }}>
                                        {stage.producedQuantity || 0} Adet
                                      </strong>
                                    </div>
                                  </div>

                                  <div className={styles.stageMachineBox}>
                                    <div style={{ fontSize: 11, color: "#6b7280" }}>Atanan Makine:</div>
                                    <div style={{ fontWeight: 600, fontSize: 13, marginTop: 2 }}>
                                      {stage.isFulfilledFromStock ? (
                                        <span style={{ color: "#6b7280" }}>— (Stoktan Kullanıldı)</span>
                                      ) : stage.machineId ? (
                                        `${stage.machineId.name || stage.machineId} (${stage.machineId.code || ""})`
                                      ) : (
                                        <span style={{ color: "#dc2626" }}>Makine Atanmamış</span>
                                      )}
                                    </div>

                                    {stage.assignedOperators && stage.assignedOperators.length > 0 && (
                                      <div style={{ marginTop: 6, fontSize: 12 }}>
                                        <span style={{ color: "#6b7280" }}>Operatör: </span>
                                        <strong>{stage.assignedOperators[0].name}</strong>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {idx < (order.stages || []).length - 1 && (
                                  <div className={styles.pipelineArrow}>
                                    <FiArrowRight />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </Table>

        {orders.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
            <FiList size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <h4>Kayıtlı İş Emri Bulunamadı</h4>
            <p>Yeni bir iş emri oluşturmak için "Yeni İş Emri Oluştur" butonunu kullanabilirsiniz.</p>
          </div>
        )}
      </CardContainer>

      {/* Detail Modal */}
      {detailModalOrder && (
        <Modal isOpen={!!detailModalOrder} onClose={() => setDetailModalOrder(null)} size="lg">
          <ModalHeader onClose={() => setDetailModalOrder(null)}>
            İş Emri Detayı: {detailModalOrder.workOrderNumber}
          </ModalHeader>
          <ModalBody>
            {detailModalOrder.myStages && detailModalOrder.myStages.length > 0 && (
              <div
                style={{
                  background: "#ecfdf5",
                  border: "1px solid #a7f3d0",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                }}
              >
                <div style={{ color: "#065f46", fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                  ★ Bu İş Emrinde Size Atanan Aşamalar:
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {detailModalOrder.myStages.map((ms, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: "#047857" }}>
                      • <strong>Aşama {ms.stageOrder}:</strong> {ms.partName} — Planlanan: {ms.plannedQuantity} Adet ({STATUS_LABELS[ms.status] || ms.status})
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <strong>Başlık:</strong> {detailModalOrder.title}
              </div>
              <div>
                <strong>Öncelik:</strong> {PRIORITY_LABELS[detailModalOrder.priority] || detailModalOrder.priority}
              </div>
              <div>
                <strong>Planlanan Miktar:</strong> {detailModalOrder.plannedQuantity} Adet
              </div>
              <div>
                <strong>Üretilen Miktar:</strong>{" "}
                {(detailModalOrder.stages && detailModalOrder.stages.length > 0
                  ? detailModalOrder.stages[detailModalOrder.stages.length - 1]?.producedQuantity || 0
                  : detailModalOrder.producedQuantity || 0)}{" "}
                Adet
              </div>
              <div>
                <strong>Hurda Miktarı:</strong> {detailModalOrder.scrapQuantity || 0} Adet
              </div>
              <div>
                <strong>Durum:</strong> {STATUS_LABELS[detailModalOrder.status] || detailModalOrder.status}
              </div>
              <div>
                <strong>Başlatma Modu:</strong>{" "}
                {canWrite ? (
                  <button
                    type="button"
                    onClick={async () => {
                      await handleAutoStartToggle(detailModalOrder._id, detailModalOrder.autoStart);
                      setDetailModalOrder((prev) => (prev ? { ...prev, autoStart: !prev.autoStart } : null));
                    }}
                    style={{
                      background:
                        detailModalOrder.autoStart !== false
                          ? "rgba(16, 185, 129, 0.12)"
                          : "rgba(107, 114, 128, 0.12)",
                      color: detailModalOrder.autoStart !== false ? "#059669" : "#4b5563",
                      border:
                        detailModalOrder.autoStart !== false
                          ? "1px solid rgba(16, 185, 129, 0.3)"
                          : "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 8px",
                      cursor: "pointer",
                      marginLeft: 6,
                    }}
                    title="Tıklayarak Otomatik / Manuel modunu değiştirin"
                  >
                    {detailModalOrder.autoStart !== false ? "Otomatik" : "Manuel"}
                  </button>
                ) : (
                  <span
                    style={{
                      background:
                        detailModalOrder.autoStart !== false
                          ? "rgba(16, 185, 129, 0.12)"
                          : "rgba(107, 114, 128, 0.12)",
                      color: detailModalOrder.autoStart !== false ? "#059669" : "#4b5563",
                      border:
                        detailModalOrder.autoStart !== false
                          ? "1px solid rgba(16, 185, 129, 0.3)"
                          : "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 8px",
                      marginLeft: 6,
                    }}
                  >
                    {detailModalOrder.autoStart !== false ? "Otomatik" : "Manuel"}
                  </span>
                )}
              </div>
            </div>

            {detailModalOrder.description && (
              <div style={{ marginBottom: 16 }}>
                <strong>Açıklama:</strong>
                <p style={{ margin: "4px 0", color: "#475569" }}>{detailModalOrder.description}</p>
              </div>
            )}

            {detailModalOrder.instructions && (
              <div style={{ marginBottom: 16 }}>
                <strong>Üretim Talimatları (SOP):</strong>
                <div className={styles.instructionBox}>{detailModalOrder.instructions}</div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {canWrite && (
              <Button
                appearance="primary"
                iconBefore={<FiEdit2 size={14} />}
                onClick={() => {
                  const targetId = detailModalOrder._id;
                  setDetailModalOrder(null);
                  navigate(`/work-orders/edit/${targetId}`);
                }}
              >
                Düzenle
              </Button>
            )}
            <Button appearance="subtle" onClick={() => setDetailModalOrder(null)}>
              Kapat
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </AppPage>
  );
};

export default withAuth(WorkOrders);
